/* Prices in USD cents. Shopify selling plans and automatic discounts charge the order. */
(() => {
  'use strict';

  function bundleReadiness(config, products, selected, samples, pickedSamples, subscription, planFor, basePrice, subPrice) {
    if (config.currency !== 'USD') return 'This offer is available in USD only.';
    if (!products.length) return 'These products are not available for purchase yet.';
    for (const index of selected) {
      if (index === null) continue;
      const item = products[index];
      if (!item?.id || !item.available) return 'This product is not available for purchase yet.';
      if (item.price !== basePrice) return 'These bundle prices are not active yet.';
      if (subscription && planFor(item)?.price !== subPrice) return 'Subscriptions for this bundle are not active yet.';
    }
    for (const index of pickedSamples) {
      if (index === null) continue;
      const sample = samples[index];
      if (!sample?.id || !sample.handle || sample.available === false) return 'The selected free sample is unavailable. Choose another scent.';
    }
    if (pickedSamples.length && !samples.some(sample => sample.id && sample.handle && sample.available !== false)) return 'Free samples for this offer are not available yet.';
    return '';
  }
  function verifyBundleCart(cart, items, bundleId, expectedUnit, currency) {
    if (cart?.currency !== currency || currency !== 'USD' || !Array.isArray(cart.items)) return false;
    const requested = new Map(items.map(item => [`${item.id}-${item.selling_plan || 0}`, item]));
    const remaining = new Map(items.map(item => [`${item.id}-${item.selling_plan || 0}`, item.quantity]));
    if (requested.size !== items.length || !Number.isSafeInteger(expectedUnit) || expectedUnit <= 0) return false;
    const lines = cart.items.filter(item => item.properties?._bundle === bundleId);
    const matches = lines.every(item => {
      const key = `${item.variant_id || item.id}-${item.selling_plan_allocation?.selling_plan?.id || 0}`;
      const source = requested.get(key);
      if (!source || !Number.isSafeInteger(item.quantity) || item.quantity < 1 || !Number.isSafeInteger(item.final_line_price)) return false;
      if (!Object.entries(source.properties || {}).every(([key,value]) => (item.properties || {})[key] === value)) return false;
      const free = source.properties?._free === '1';
      const policy = typeof window !== 'undefined' && window.HSOfferPolicy;
      const nativePrices = policy?.allowedUnitPrices(cart,item) || [];
      const validPrices = nativePrices.length ? nativePrices : [expectedUnit];
      if ((item.properties?._free === '1') !== free || !(free ? item.final_line_price === 0 : validPrices.some(value => item.final_line_price === value * item.quantity))) return false;
      const left = remaining.get(key) - item.quantity;
      remaining.set(key, left);
      return left >= 0;
    });
    return matches && [...remaining.values()].every(n => n === 0);
  }
  async function addBundleSafely({items, bundleId, currency, expectedUnit, cartUrl, addUrl, notify = () => {}}) {
    if (currency !== 'USD' || !items.length || items.some(item => !Number.isSafeInteger(Number(item.id)) || Number(item.id) <= 0 || !Number.isSafeInteger(item.quantity) || item.quantity < 1 || item.properties?._bundle !== bundleId)) throw new Error('This bundle is not available. Please review your selection.');
    let success = false, rollbackFailed = false;
    const json = async (url, options) => {
      const response = await fetch(url, options);
      let data;
      try { data = await response.json(); } catch (_) { throw new Error('The cart could not be verified. Please try again.'); }
      if (!response.ok) throw new Error(data.description || data.message || 'The bundle could not be added. Please try again.');
      return data;
    };
    const readCart = () => json(cartUrl + '.js', {headers:{Accept:'application/json'}, cache:'no-store'});
    const post = data => ({method:'POST', headers:{'Content-Type':'application/json', Accept:'application/json'}, body:JSON.stringify(data)});
    notify({id:bundleId, phase:'start'});
    try {
      await json(addUrl + '.js', post({items}));
      const cart = await readCart();
      if (!verifyBundleCart(cart, items, bundleId, expectedUnit, currency)) throw new Error('The bundle price or gifts could not be verified. Please try again.');
      success = true;
      return cart;
    } catch (error) {
      try {
        const cart = await readCart();
        if (!Array.isArray(cart.items)) throw new Error('Invalid cart');
        const updates = Object.fromEntries(cart.items.filter(item => item.properties?._bundle === bundleId).map(item => [item.key, 0]));
        if (Object.keys(updates).length) {
          await json(cartUrl + '/update.js', post({updates}));
          const clean = await readCart();
          if (!Array.isArray(clean.items) || clean.items.some(item => item.properties?._bundle === bundleId)) throw new Error('Incomplete rollback');
        }
      } catch (_) { rollbackFailed = true; }
      if (rollbackFailed) throw new Error('This bundle could not be verified. Review your cart before checkout.');
      throw error;
    } finally { notify({id:bundleId, phase:'end', success, rollbackFailed}); }
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = {bundleReadiness, verifyBundleCart, addBundleSafely};
  if (typeof document === 'undefined') return;
  const root = document.querySelector('[data-soap-bundle]');
  const json = document.querySelector('[data-soap-bundle-data]');
  if (!root || !json || root.dataset.initialized) return;
  root.dataset.initialized = 'true';
  const config = JSON.parse(json.textContent);
  const ui = window.HSBundleUI(config.ui);
  const soaps = config.soaps.filter(p => config.preview || p.available);
  const samples = config.samples;
  const current = Math.max(0, soaps.findIndex(p => p.handle === config.current));
  const state = {mode:'subscription', tier:1, quantity:6, weeks:4, soaps:[current], samples:[], busy:false};
  const $ = selector => root.querySelector(selector);
  const money = cents => new Intl.NumberFormat('en-US', {style:'currency', currency:'USD'}).format(cents / 100);
  const escape = value => String(value).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  const isSub = () => state.mode === 'subscription';
  const count = () => state.tier === 6 ? state.quantity : state.tier;
  const unit = (n, mode = state.mode) => (n >= 6 ? 1599 : n === 3 ? 1799 : 1999) - (mode === 'subscription' ? 200 : 0);
  const gifts = (n, mode = state.mode) => mode === 'subscription' ? (n >= 6 ? n - 3 : n === 3 ? 2 : 1) : (n >= 6 ? 2 : n === 3 ? 1 : 0);
  const planFor = item => {
    const paid = state.soaps.find(i => i !== null);
    const anchor = (soaps[paid ?? current]?.plans || []).find(p => p.appId === 'appstle' && p.name === `Every ${state.weeks} weeks` && p.price === 1799);
    return (item.plans || []).find(p => p.appId === 'appstle' && p.id === anchor?.id && p.price === (item.price === 0 ? 0 : 1799));
  };
  const resize = (list, n) => { list.length = Math.min(list.length, n); while (list.length < n) list.push(null); };
  let opener, pickerKind = 'soap', pickerIndex = 0;
  const dialog = $('.sb-picker');
  function render() {
    const n = count(), giftCount = gifts(n), total = unit(n) * n;
    resize(state.soaps, n); resize(state.samples, giftCount);
    root.dataset.mode = state.mode;
    root.querySelectorAll('[data-soap-mode]').forEach(button => {
      const active = button.dataset.soapMode === state.mode;
      button.classList.toggle('on', active); button.setAttribute('aria-pressed', String(active));
    });
    $('[data-soap-frequency]').hidden = !isSub();
    ui.updatePlans(root, {kind:'soap', state, n, unit, gifts, money});
    $('[data-soap-tiers]').innerHTML = ui.render({kind:'soap', tiers:[1, 3, 6], state, n, unit, gifts, money, products:soaps, selected:state.soaps, samples, lead:true});
    const unavailable = bundleReadiness(config, soaps, state.soaps, samples, state.samples, isSub(), planFor, 1999, 1799);
    const ready = !unavailable;
    const button = $('[data-soap-bundle-add]');
    button.disabled = !ready || state.busy;
    button.textContent = state.busy ? 'Adding…' : `${isSub() ? 'Subscribe' : 'Add to cart'} — ${money(total)}`;
    $('[data-soap-notice]').textContent = unavailable;
    root.querySelectorAll('button:not([data-soap-bundle-add]), input, select').forEach(control => {
      control.disabled = state.busy || (control.dataset.soapStep === '-1' && n <= 6);
    });
  }
  function showPicker(button) {
    opener = button; pickerKind = button.dataset.pick; pickerIndex = Number(button.dataset.slot);
    const catalogue = pickerKind === 'soap' ? soaps : samples;
    const selected = pickerKind === 'soap' ? state.soaps[pickerIndex] : state.samples[pickerIndex];
    $('#soapBundlePickerTitle').textContent = pickerKind === 'soap' ? `Choose soap ${pickerIndex + 1}` : `Choose free sample ${pickerIndex + 1}`;
    $('[data-bundle-picker-options]').innerHTML = ui.picker(catalogue, selected);
    dialog.showModal();
  }
  const setQuantity = value => { const n = Number(value); state.quantity = Number.isSafeInteger(n) ? Math.max(6,n) : 6; };
  root.addEventListener('change', event => {
    if (state.busy) return;
    if (event.target.id === 'soapPackQuantity') setQuantity(event.target.value);
    else if (event.target.id === 'soapFrequency') state.weeks = Number(event.target.value);
    else return;
    render();
  });
  root.addEventListener('click', event => {
    if (state.busy) return;
    const mode = event.target.closest('[data-soap-mode]');
    if (mode) { state.mode = mode.dataset.soapMode; render(); return; }
    const tier = event.target.closest('[data-soap-tier]');
    if (tier) { state.tier = Number(tier.dataset.soapTier); render(); $(`[data-soap-tier="${state.tier}"]`).focus({preventScroll:true}); return; }
    const step = event.target.closest('[data-soap-step]');
    if (step) { setQuantity(state.quantity + Number(step.dataset.soapStep)); render(); $(`[data-soap-step="${step.dataset.soapStep}"]`).focus({preventScroll:true}); return; }
    const pick = event.target.closest('[data-pick]');
    if (pick) { showPicker(pick); return; }
    const choice = event.target.closest('[data-choice]');
    if (choice) {
      (pickerKind === 'soap' ? state.soaps : state.samples)[pickerIndex] = Number(choice.dataset.choice);
      dialog.close(); render(); $(`[data-pick="${pickerKind}"][data-slot="${pickerIndex}"]`).focus({preventScroll:true});
    }
    if (event.target.closest('[data-bundle-picker-close]')) dialog.close();
  });
  dialog.addEventListener('close', () => { if (opener?.isConnected) opener.focus({preventScroll:true}); });
  dialog.addEventListener('click', event => { if (event.target === dialog) { const r = dialog.getBoundingClientRect(); if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) dialog.close(); } });
  $('[data-soap-bundle-add]').addEventListener('click', async () => {
    if (state.busy || $('[data-soap-bundle-add]').disabled) return;
    for (const [kind, list] of [['soap',state.soaps],['sample',state.samples]]) {
      const missing = list.findIndex(i => i === null);
      if (missing !== -1) { showPicker($(`[data-pick="${kind}"][data-slot="${missing}"]`)); return; }
    }
    const unavailable = bundleReadiness(config, soaps, state.soaps, samples, state.samples, isSub(), planFor, 1999, 1799);
    if (unavailable) { $('[data-soap-notice]').textContent = unavailable; return; }
    state.busy = true; render();
    // r14 : ce script tourne maintenant avant hs-offer-policy.js et le panier : l'ajout les attend (bouton « Adding… »).
    await ui.ready?.();
    const bid = `soap-${Date.now()}-${Math.random().toString(36).slice(2,8)}`;
    const grouped = new Map();
    const properties = {_bundle:bid, _soap_bundle:'1', _soap_quantity:String(count())};
    const add = p => {
      const plan = isSub() ? planFor(p) : null;
      const key = `${p.id}-${plan?.id || 0}`;
      if (grouped.has(key)) { grouped.get(key).quantity++; return; }
      const item = {id:Number(p.id),quantity:1,properties:{...properties}};
      if (plan) item.selling_plan = Number(plan.id);
      grouped.set(key, item);
    };
    state.soaps.forEach(i => add(soaps[i]));
    const chosenSamples = state.samples.map(i => samples[i]);
    // Samples are contents of the paid offer, never zero-priced standalone cart items.
    // Preferences do not authorize a quantity; fulfillment derives the quota from paid merchandise.
    if (chosenSamples.length) {
      const parent = grouped.values().next().value;
      parent.properties._hs_sample_preferences = JSON.stringify(chosenSamples.map(sample => sample.handle));
      parent.properties['Included sample scents'] = chosenSamples.map(sample => sample.name).join(', ');
    }
    const prefix = window.Shopify?.routes?.root || '/';
    try {
      await addBundleSafely({
        items:[...grouped.values()], bundleId:bid, currency:config.currency,
        expectedUnit:unit(count()), cartUrl:`${prefix}cart`, addUrl:`${prefix}cart/add`,
        notify:detail => document.dispatchEvent(new CustomEvent('hs:bundle-transaction', {detail}))
      });
      // r14 : tiroir pas encore prêt -> on l'attend (toujours « Adding… ») au lieu de partir sur /cart.
      const drawer = ui.cartReady ? await ui.cartReady() : typeof window.hsCartOpen === 'function';
      state.busy = false; render();
      if (drawer && typeof window.hsCartOpen === 'function') window.hsCartOpen();
      else window.location.assign(`${prefix}cart`);
    } catch (error) {
      state.busy = false; render(); $('[data-soap-notice]').textContent = error.message;
    }
  });
  render();
})();


